import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, Box, Typography, IconButton, useMediaQuery, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  PictureAsPdf as PdfIcon,
  Stop as StopIcon,
  Pause as PauseIcon,
  PlayArrow as ResumeIcon,
} from '@mui/icons-material';
import { useChronometer } from '../context/ChronometerContext';
import { Viewer, Worker, SpecialZoomLevel } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';

const formatTime = (ms) => {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

function DocumentViewerModal({ open, onClose, documentUrl, onStop }) {
  const { time, isPaused, pauseTimer, resumeTimer } = useChronometer();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [pdfData, setPdfData] = useState(null);

  const isImage = Boolean(documentUrl) && /\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i.test(documentUrl);

  // Download managers (IDM…) hijack native PDF navigations/iframes; fetching the bytes and rendering with pdf.js avoids that
  useEffect(() => {
    setPdfData(null);
    if (!open || !documentUrl || isImage) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(documentUrl, { cache: 'no-store' });
        if (!res.ok) throw new Error(`Failed to fetch document: ${res.status}`);
        const bytes = new Uint8Array(await res.arrayBuffer());
        if (!cancelled) setPdfData(bytes);
      } catch (err) {
        console.error('Document fetch failed, falling back to URL', err);
        if (!cancelled) setPdfData(documentUrl);
      }
    })();
    return () => { cancelled = true; };
  }, [open, documentUrl, isImage]);

  if (!documentUrl) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: isMobile ? 'calc(100vw - 16px)' : '92vw',
          height: isMobile ? '75dvh' : '92vh',
          maxWidth: isMobile ? 'calc(100vw - 16px)' : '92vw',
          maxHeight: isMobile ? '75dvh' : '92vh',
          borderRadius: isMobile ? '8px' : '20px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          m: 0,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          px: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <PdfIcon sx={{ color: 'white', fontSize: 22 }} />
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '16px' }}>
          وثيقة المراجعة
        </Typography>
      </Box>

      {/* Document area */}
      <DialogContent sx={{ p: 0, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {isImage ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#1a1a2e',
              p: 3,
              minHeight: 0,
            }}
          >
            <img
              src={documentUrl}
              alt="وثيقة المراجعة"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
              }}
            />
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, height: '100%', bgcolor: '#e5e7eb' }}>
            {pdfData ? (
              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                <Viewer
                  fileUrl={pdfData}
                  // pdf.js detaches the buffer it receives; hand it a copy so re-reads still work
                  transformGetDocumentParams={(params) => ({
                    ...params,
                    data: typeof params.data?.slice === 'function' ? params.data.slice() : params.data,
                  })}
                  defaultScale={SpecialZoomLevel.PageWidth}
                />
              </Worker>
            ) : (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: '#667eea' }} />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Chronometer footer */}
      <Box
        sx={{
          flexShrink: 0,
          px: 1.5,
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {/* Pill — only the controls get the color */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1.5,
            px: 1.5,
            py: 0.5,
            borderRadius: '20px',
            background: isPaused
              ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            transition: 'background 0.3s ease',
          }}
        >
          {/* Pause / Resume */}
          <IconButton
            onClick={isPaused ? resumeTimer : pauseTimer}
            sx={{
              width: 26, height: 26,
              bgcolor: 'rgba(255,255,255,0.18)',
              color: 'white',
              border: '1.5px solid rgba(255,255,255,0.35)',
              transition: 'all 0.2s ease',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.32)', transform: 'scale(1.08)' },
            }}
          >
            {isPaused ? <ResumeIcon sx={{ fontSize: 13 }} /> : <PauseIcon sx={{ fontSize: 13 }} />}
          </IconButton>

          {/* Timer display */}
          <Typography
            sx={{
              color: 'white',
              fontWeight: 800,
              fontSize: '18px',
              fontFamily: 'monospace',
              letterSpacing: '2px',
              lineHeight: 1,
            }}
          >
            {formatTime(time)}
          </Typography>

          <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 600, fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase' }}>
            {isPaused ? 'متوقف' : 'قيد التنفيذ'}
          </Typography>

          {/* Stop */}
          <IconButton
            onClick={onStop}
            sx={{
              width: 26, height: 26,
              bgcolor: 'rgba(239,68,68,0.25)',
              color: 'white',
              border: '1.5px solid rgba(239,68,68,0.6)',
              transition: 'all 0.2s ease',
              '&:hover': { bgcolor: '#ef4444', borderColor: '#ef4444', transform: 'scale(1.08)' },
            }}
          >
            <StopIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </Box>
      </Box>
    </Dialog>
  );
}

export default DocumentViewerModal;
